import React, { useState } from "react";
import * as XLSX from "xlsx";
import { Modal } from "flowbite-react";
import { IoMdAdd, IoMdTrash, IoMdClose } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { MONITORED_CLIENT } from "../config/apiConfig";
import { toast } from "react-toastify";
import { useApplication } from "../contexts/ApplicationContext";
import {
  MdRadar,
  MdVerifiedUser,
  MdNotificationsActive,
  MdTrendingUp,
  MdPersonSearch,
  MdHistory,
  MdAccountBalanceWallet,
  MdGavel,
  MdMoneyOff,
  MdCreditScore,
  MdNotificationImportant,
  MdWarningAmber,
  MdManageAccounts,
  MdEmail,
  MdCheckCircle,
  MdDoneAll,
  MdMarkEmailRead,
  MdUploadFile,
  MdErrorOutline,
  MdContentCopy,
  MdDownload,
} from "react-icons/md";
import { maskCPF, maskCNPJ } from "../utils/masks";
import "./MonitoreClientesPage.css";

const BENEFITS = [
  {
    icon: MdRadar,
    title: "Monitoramento Contínuo",
    desc: "Acompanhe seus clientes em tempo real e seja avisado sobre qualquer alteração no perfil de crédito ou cadastral.",
    color: "blue",
  },
  {
    icon: MdNotificationsActive,
    title: "Alertas Instantâneos",
    desc: "Receba notificações imediatas quando surgir uma nova negativação, protesto ou ocorrência relevante.",
    color: "amber",
  },
  {
    icon: MdVerifiedUser,
    title: "Gestão de Risco Proativa",
    desc: "Antecipe inadimplências e tome decisões mais seguras antes que problemas afetem o seu negócio.",
    color: "emerald",
  },
  {
    icon: MdTrendingUp,
    title: "Inteligência Estratégica",
    desc: "Utilize os dados monitorados para criar estratégias de cobrança e crédito mais eficientes e personalizadas.",
    color: "violet",
  },
];

const MIN_MONITORING_MONTHS = 3;

function applyDocMask(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 11) return maskCPF(digits);
  return maskCNPJ(digits.slice(0, 14));
}

function validateCPF(cpf) {
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(d[i]) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== Number(d[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(d[i]) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === Number(d[10]);
}

function validateCNPJ(cnpj) {
  const d = cnpj.replace(/\D/g, "");
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const calc = (d, len) => {
    let sum = 0,
      pos = len - 7;
    for (let i = len; i >= 1; i--) {
      sum += Number(d[len - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(d, 12) === Number(d[12]) && calc(d, 13) === Number(d[13]);
}

function validateDoc(digits) {
  if (digits.length === 11) return validateCPF(digits);
  if (digits.length === 14) return validateCNPJ(digits);
  return false;
}

function parseBulkText(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const seen = new Set();
  const rows = [];

  for (const line of lines) {
    const parts = line.split(/[,;\t]/).map((p) => p.trim());
    const rawDoc = parts[0] || "";
    const nome = parts[1] || "";
    const digits = rawDoc.replace(/\D/g, "");

    if (!digits) {
      if (/^(cpf|cnpj|documento)/i.test(rawDoc)) continue; // cabeçalho
      continue;
    }

    const tipo =
      digits.length === 11 ? "CPF" : digits.length === 14 ? "CNPJ" : null;
    const valid = Boolean(tipo) && validateDoc(digits);
    const duplicate = seen.has(digits);
    if (!duplicate) seen.add(digits);

    rows.push({ documento: digits, nome, tipo, valid, duplicate });
  }

  return rows;
}

function hasTruthyFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function getMinimumMonitoringEndDate(createdAt) {
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return null;

  const minimumEndDate = new Date(createdDate);
  minimumEndDate.setMonth(minimumEndDate.getMonth() + MIN_MONITORING_MONTHS);
  return minimumEndDate;
}

function canRemoveMonitoring(client) {
  const minimumEndDate = getMinimumMonitoringEndDate(client?.createdAt);
  if (!minimumEndDate) return true;
  return new Date() >= minimumEndDate;
}

function getMinimumMonitoringMessage(client) {
  const minimumEndDate = getMinimumMonitoringEndDate(client?.createdAt);
  const dateText = minimumEndDate
    ? ` Este monitoramento poderá ser removido a partir de ${minimumEndDate.toLocaleDateString("pt-BR")}.`
    : "";

  return `O período mínimo na carteira é de ${MIN_MONITORING_MONTHS} meses.${dateText}`;
}

function HistoryModal({ client, onClose }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(
    Number(client?.unreadUpdatesCount || 0) > 0 ? "updates" : "history",
  );
  const [markingId, setMarkingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const historyQueryKey = ["monitored-client-history-user", client.id];

  const { data, isLoading } = useQuery(historyQueryKey, async () => {
    const { data } = await axios.get(
      MONITORED_CLIENT.GET_HISTORY_USER(client.id),
    );
    return data.data;
  });

  const entries = data || [];
  const unreadEntries = entries.filter((entry) => !entry.readAt);
  const visibleEntries = activeTab === "updates" ? unreadEntries : entries;

  const invalidateUpdates = () => {
    queryClient.invalidateQueries(historyQueryKey);
    queryClient.invalidateQueries(["monitored-clients"]);
  };

  const handleMarkRead = async (historyId) => {
    setMarkingId(historyId);
    try {
      await axios.patch(
        MONITORED_CLIENT.MARK_HISTORY_READ(client.id, historyId),
      );
      invalidateUpdates();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Erro ao marcar atualização como lida.",
      );
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.patch(MONITORED_CLIENT.MARK_ALL_HISTORY_READ(client.id));
      invalidateUpdates();
      setActiveTab("history");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Erro ao marcar atualizações como lidas.",
      );
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between p-3 border-b border-slate-100">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">
              Atualizações do cliente
            </h2>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[320px]">
              {client.nome || applyDocMask(client.documento)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unreadEntries.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markingAll}
                className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition disabled:opacity-50"
              >
                <MdDoneAll size={14} />
                {markingAll ? "Marcando..." : "Marcar todas"}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition"
            >
              <IoMdClose size={20} />
            </button>
          </div>
        </div>

        <div className="px-3 pt-3 border-b border-slate-100">
          <div className="inline-flex rounded-xl bg-slate-100 p-1">
            <button
              onClick={() => setActiveTab("updates")}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                activeTab === "updates"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <MdMarkEmailRead size={14} />
              Atualizações
              {unreadEntries.length > 0 && (
                <span className="min-w-[18px] rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] leading-none text-white">
                  {unreadEntries.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                activeTab === "history"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <MdHistory size={14} />
              Histórico
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-8 text-slate-400 text-sm gap-2">
              <svg
                className="animate-spin h-4 w-4 text-blue-500"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              Carregando...
            </div>
          ) : visibleEntries.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2 text-slate-400">
              {activeTab === "updates" ? (
                <MdCheckCircle size={32} className="opacity-25" />
              ) : (
                <MdHistory size={32} className="opacity-25" />
              )}
              <p className="text-sm">
                {activeTab === "updates"
                  ? "Nenhuma atualização pendente."
                  : "Nenhuma entrada no histórico."}
              </p>
            </div>
          ) : (
            visibleEntries.map((e) => {
              const isUnread = !e.readAt;

              return (
                <div
                  key={e.id}
                  className={`rounded-xl border p-3 ${
                    isUnread
                      ? "border-blue-200 bg-blue-50/70"
                      : "border-slate-100 bg-slate-50"
                  }`}
                >
                  <div className="mb-2 flex flex-wrap items-center gap-1.5">
                    {isUnread ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Nova atualização
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                        <MdCheckCircle size={11} />
                        Lida
                      </span>
                    )}
                    {e.tipo && (
                      <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">
                        {e.tipo}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {e.nota}
                  </p>
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {new Date(e.createdAt).toLocaleString("pt-BR")}
                    </span>
                    {isUnread ? (
                      <button
                        onClick={() => handleMarkRead(e.id)}
                        disabled={markingId === e.id}
                        className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 transition disabled:opacity-50"
                      >
                        <MdCheckCircle size={13} />
                        {markingId === e.id
                          ? "Marcando..."
                          : "Marcar como lida"}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <MdCheckCircle size={13} />
                        {e.readAt
                          ? `Lida em ${new Date(e.readAt).toLocaleString("pt-BR")}`
                          : "Lida"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const MonitoreClientesPage = () => {
  const queryClient = useQueryClient();
  const { user } = useApplication();
  const hasClientMonitoringAccess = hasTruthyFlag(
    user?.Company?.hasClientMonitoring,
  );

  // ── Clientes ──────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);
  const [nome, setNome] = useState("");
  const [documento, setDocumento] = useState("");
  const [docError, setDocError] = useState("");

  const { data: clientes = [], isLoading: loadingClientes } = useQuery(
    ["monitored-clients"],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.LIST);
      return data?.data || [];
    },
    { enabled: hasClientMonitoringAccess },
  );

  const createClientMutation = useMutation(
    async (payload) => {
      const { data } = await axios.post(MONITORED_CLIENT.CREATE, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["monitored-clients"]);
        setNome("");
        setDocumento("");
        setDocError("");
        setIsModalOpen(false);
      },
      onError: () => toast.error("Erro ao adicionar cliente."),
    },
  );

  // ── Importação em massa ──────────────────────────────────────
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkFileName, setBulkFileName] = useState("");
  const fileInputRef = React.useRef(null);

  const bulkRows = React.useMemo(() => parseBulkText(bulkText), [bulkText]);
  const bulkValidRows = bulkRows.filter((r) => r.valid && !r.duplicate);

  const bulkCreateMutation = useMutation(
    async (clientes) => {
      const { data } = await axios.post(MONITORED_CLIENT.BULK_CREATE, {
        clientes,
      });
      return data?.data || [];
    },
    {
      onSuccess: (results) => {
        queryClient.invalidateQueries(["monitored-clients"]);
        const created = results.filter((r) => r.status === "created").length;
        const duplicated = results.filter(
          (r) => r.status === "duplicate",
        ).length;
        const failed = results.filter((r) => r.status === "error").length;

        if (created > 0) {
          toast.success(
            `${created} cliente${created !== 1 ? "s" : ""} importado${created !== 1 ? "s" : ""} com sucesso.`,
          );
        }
        if (duplicated > 0) {
          toast.info(`${duplicated} já estavam cadastrados e foram ignorados.`);
        }
        if (failed > 0) {
          toast.error(
            `${failed} registro${failed !== 1 ? "s" : ""} não pôde${failed !== 1 ? "ram" : ""} ser importado${failed !== 1 ? "s" : ""}.`,
          );
        }

        setBulkText("");
        setBulkFileName("");
        setIsBulkModalOpen(false);
      },
      onError: () => toast.error("Erro ao importar clientes em massa."),
    },
  );

  const handleDownloadTemplate = () => {
    const rows = [
      ["CPF/CNPJ", "Nome"],
      ["123.456.789-00", "João Silva"],
      ["12.345.678/0001-90", "Empresa Exemplo Ltda"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 22 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, "modelo_importacao_clientes.xlsx");
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFileName(file.name);
    const ext = file.name.split(".").pop()?.toLowerCase();
    const reader = new FileReader();

    if (ext === "xlsx" || ext === "xls") {
      reader.onload = (ev) => {
        try {
          const workbook = XLSX.read(new Uint8Array(ev.target.result), {
            type: "array",
          });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            raw: false,
            defval: "",
          });
          const lines = rows
            .map(([doc, nome]) =>
              [doc, nome].map((v) => String(v ?? "").trim()).join("\t"),
            )
            .filter((line) => line.replace(/\t/g, "").trim() !== "");
          setBulkText(lines.join("\n"));
        } catch (error) {
          toast.error("Não foi possível ler a planilha. Verifique o arquivo.");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (ev) => setBulkText(String(ev.target?.result || ""));
      reader.readAsText(file);
    }

    e.target.value = "";
  };

  const handleBulkSubmit = () => {
    if (!hasClientMonitoringAccess) {
      toast.info("Solicite à equipe a liberação do Monitore Clientes.");
      return;
    }
    if (bulkValidRows.length === 0) return;
    bulkCreateMutation.mutate(
      bulkValidRows.map((r) => ({
        documento: r.documento,
        nome: r.nome || null,
      })),
    );
  };

  const removeClientMutation = useMutation(
    async (id) => axios.delete(MONITORED_CLIENT.REMOVE(id)),
    {
      onSuccess: () => queryClient.invalidateQueries(["monitored-clients"]),
      onError: (error) =>
        toast.error(
          error?.response?.data?.message || "Erro ao remover cliente.",
        ),
    },
  );

  const handleDocChange = (e) => {
    setDocumento(applyDocMask(e.target.value));
    setDocError("");
  };

  const handleAdd = () => {
    if (!hasClientMonitoringAccess) {
      toast.info("Solicite à equipe a liberação do Monitore Clientes.");
      return;
    }

    const digits = documento.replace(/\D/g, "");
    if (!validateDoc(digits)) {
      const tipo = digits.length <= 11 ? "CPF" : "CNPJ";
      setDocError(`${tipo} inválido. Verifique os dígitos informados.`);
      return;
    }
    createClientMutation.mutate({
      documento: digits,
      tipo: digits.length === 11 ? "CPF" : "CNPJ",
      nome: nome.trim() || null,
    });
  };

  // ── Carteira / E-mails ────────────────────────────────────────
  const [isCarteirModalOpen, setIsCarteiraModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [localEmails, setLocalEmails] = useState([]);

  const { isLoading: loadingEmails } = useQuery(
    ["monitored-client-emails"],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.GET_EMAILS);
      return data?.data || [];
    },
    {
      enabled: isCarteirModalOpen,
      onSuccess: (data) => setLocalEmails(data.map((e) => e.email)),
    },
  );

  const saveEmailsMutation = useMutation(
    async (emails) => axios.put(MONITORED_CLIENT.SAVE_EMAILS, { emails }),
    {
      onSuccess: () => {
        toast.success("E-mails salvos com sucesso.");
        setIsCarteiraModalOpen(false);
        setEmailInput("");
        setEmailError("");
      },
      onError: () => toast.error("Erro ao salvar e-mails."),
    },
  );

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleAddEmail = () => {
    const val = emailInput.trim();
    if (!isValidEmail(val)) {
      setEmailError("Informe um e-mail válido.");
      return;
    }
    if (localEmails.includes(val)) {
      setEmailError("Este e-mail já foi adicionado.");
      return;
    }
    setLocalEmails((prev) => [...prev, val]);
    setEmailInput("");
    setEmailError("");
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleRemoveEmail = (email) => {
    setLocalEmails((prev) => prev.filter((e) => e !== email));
  };

  return (
    <div className="space-y-6">
      {/* Hero / Benefits */}
      <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-slate-50 to-blue-50/30 shadow-sm px-4 py-3">
        <h1 className="text-2xl font-bold text-slate-800 leading-snug">
          Tenha o controle total sobre{" "}
          <span className="text-blue-600">quem você está negociando</span>
        </h1>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">
          Com o{" "}
          <strong className="text-slate-700">Gerenciamento de Carteira</strong>,
          você cadastra os CPFs e CNPJs dos seus parceiros, clientes ou
          fornecedores e passa a receber alertas automáticos sobre mudanças no
          perfil de crédito, novas negativações e eventos cadastrais relevantes.
          Reduza a inadimplência, proteja sua carteira e tome decisões com muito
          mais segurança.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className={`benefit-card ${b.color}`}>
                <span className="step-num">0{i + 1}</span>
                <div className={`benefit-icon ${b.color}`}>
                  <Icon size={17} />
                </div>
                <p className="benefit-title">{b.title}</p>
                <p className="benefit-desc">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aviso período mínimo */}
      <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <MdWarningAmber
          size={18}
          className="text-amber-500 flex-shrink-0 mt-0.5"
        />
        <p className="text-[12px] text-amber-800 leading-relaxed">
          <strong className="font-semibold">Período mínimo de 3 meses.</strong>{" "}
          Uma vez adicionado o cliente à carteira, o contrato de monitoramento
          tem vigência mínima de 3 meses. O cancelamento antes desse prazo{" "}
          <span className="font-semibold">não isenta a cobrança</span> — o valor
          integral dos 3 meses será cobrado normalmente.{" "}
          <span className="italic">
            Esta é uma política do birô de crédito, não da nossa plataforma.
          </span>
        </p>
      </div>

      {/* Clients table */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="text-[15px] font-semibold text-slate-800">
              Clientes Monitorados
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {clientes.length} cliente{clientes.length !== 1 ? "s" : ""}{" "}
              cadastrado{clientes.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsCarteiraModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.97] transition-all px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm"
            >
              <MdManageAccounts size={18} />
              Gerenciar Carteira
            </button>
            <button
              onClick={() => {
                if (hasClientMonitoringAccess) setIsBulkModalOpen(true);
              }}
              disabled={!hasClientMonitoringAccess}
              title={
                hasClientMonitoringAccess
                  ? "Importar clientes em massa"
                  : "Solicite à equipe a liberação do Monitore Clientes."
              }
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition-all ${
                hasClientMonitoringAccess
                  ? "border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.97] text-slate-600"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}
            >
              <MdUploadFile size={18} />
              Importar em Massa
            </button>
            <button
              onClick={() => {
                if (hasClientMonitoringAccess) setIsModalOpen(true);
              }}
              disabled={!hasClientMonitoringAccess}
              title={
                hasClientMonitoringAccess
                  ? "Adicionar cliente"
                  : "Solicite à equipe a liberação do Monitore Clientes."
              }
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition-all ${
                hasClientMonitoringAccess
                  ? "bg-blue-600 hover:bg-blue-700 active:scale-[0.97] text-white"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}
            >
              <IoMdAdd size={18} />
              Adicionar Cliente
            </button>
          </div>
        </div>

        {!hasClientMonitoringAccess && (
          <div className="mx-5 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Para adicionar clientes monitorados, solicite à equipe a liberação
            do Monitore Clientes para a sua empresa.
          </div>
        )}

        {loadingClientes ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2">
            <svg
              className="animate-spin h-4 w-4 text-blue-500"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            Carregando...
          </div>
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
            <MdRadar size={40} className="opacity-30" />
            <p className="text-sm">Nenhum cliente monitorado ainda.</p>
            <p className="text-xs text-slate-300">
              {hasClientMonitoringAccess
                ? 'Clique em "Adicionar Cliente" para começar.'
                : "Solicite à equipe a liberação para começar."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 font-semibold">
                    Nome / Razão Social
                  </th>
                  <th className="px-5 py-3 font-semibold">Documento</th>
                  <th className="px-5 py-3 font-semibold">Tipo</th>
                  <th className="px-5 py-3 font-semibold">Adicionado em</th>
                  <th className="px-5 py-3 font-semibold">Atualizações</th>
                  <th className="px-5 py-3 font-semibold text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clientes.map((c) => {
                  const unreadUpdatesCount = Number(c.unreadUpdatesCount || 0);
                  const hasUnreadUpdates = unreadUpdatesCount > 0;
                  const canRemoveClient = canRemoveMonitoring(c);

                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-slate-50/70 transition-colors"
                    >
                      <td className="px-5 py-3.5 font-medium text-slate-700">
                        {c.nome || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-slate-600 font-mono">
                        {applyDocMask(c.documento)}
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                            c.tipo === "CPF"
                              ? "bg-blue-50 text-blue-700"
                              : "bg-violet-50 text-violet-700"
                          }`}
                        >
                          {c.tipo}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-400 text-xs">
                        {new Date(c.createdAt).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="px-5 py-3.5">
                        {hasUnreadUpdates ? (
                          <button
                            onClick={() => setHistoryClient(c)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition"
                            title="Ver atualizações pendentes"
                          >
                            <span className="h-2 w-2 rounded-full bg-blue-600" />
                            {unreadUpdatesCount} nova
                            {unreadUpdatesCount !== 1 ? "s" : ""}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                            <MdCheckCircle size={13} />
                            Em dia
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setHistoryClient(c)}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                              hasUnreadUpdates
                                ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                            title="Ver atualizações"
                          >
                            <MdMarkEmailRead size={13} />
                            Atualizações
                          </button>
                          <button
                            onClick={() => {
                              if (!canRemoveClient) {
                                toast.info(getMinimumMonitoringMessage(c));
                                return;
                              }
                              removeClientMutation.mutate(c.id);
                            }}
                            disabled={removeClientMutation.isLoading}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-40 ${
                              canRemoveClient
                                ? "text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                                : "text-slate-300 bg-slate-50 cursor-not-allowed"
                            }`}
                            title={
                              canRemoveClient
                                ? "Remover"
                                : getMinimumMonitoringMessage(c)
                            }
                          >
                            <IoMdTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      <Modal
        show={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setDocError("");
        }}
        size="md"
      >
        <Modal.Header className="py-2">
          <div className="text-base mt-1">
            Adicionar Cliente para Monitoramento
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome / Razão Social{" "}
                <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: João Silva ou Empresa Ltda"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                CPF / CNPJ <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={documento}
                onChange={handleDocChange}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength={18}
                className={`w-full rounded-xl border ${
                  docError
                    ? "border-rose-300 bg-rose-50"
                    : "border-slate-200 bg-slate-50"
                } px-3.5 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-mono`}
              />
              {docError && (
                <p className="mt-1.5 text-xs text-rose-500">{docError}</p>
              )}
            </div>

            {/* O que será monitorado */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
              <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-widest mb-2.5">
                O que será monitorado
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {[
                  { icon: MdPersonSearch, label: "Dados cadastrais" },
                  { icon: MdHistory, label: "Histórico de consultas" },
                  {
                    icon: MdAccountBalanceWallet,
                    label: "Pendências financeiras",
                  },
                  { icon: MdGavel, label: "Protestos" },
                  { icon: MdMoneyOff, label: "Cheques sem fundos" },
                  { icon: MdCreditScore, label: "Crédito concedido" },
                  {
                    icon: MdNotificationImportant,
                    label: "Alerta de documento",
                  },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Icon size={11} className="text-white" />
                    </div>
                    <span className="text-[11px] text-slate-600 leading-none">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-3">
          <div className="flex w-full justify-end gap-2">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setDocError("");
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleAdd}
              disabled={createClientMutation.isLoading}
              className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition"
            >
              <IoMdAdd size={16} />
              {createClientMutation.isLoading ? "Salvando..." : "Adicionar"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal Importação em Massa */}
      <Modal
        show={isBulkModalOpen}
        onClose={() => {
          setIsBulkModalOpen(false);
          setBulkText("");
          setBulkFileName("");
        }}
        size="2xl"
      >
        <Modal.Header className="py-2">
          <div className="text-base mt-1">Importar Clientes em Massa</div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
              <p className="text-[12px] text-blue-800 leading-relaxed">
                Envie uma planilha Excel (.xlsx/.xls) ou um arquivo CSV com o
                CPF/CNPJ na primeira coluna e, opcionalmente, o nome na segunda
                coluna. Validamos automaticamente todos os documentos antes da
                importação.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
              >
                <MdDownload size={14} />
                Baixar planilha modelo
              </button>
            </div>

            <div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
              >
                <MdUploadFile size={28} className="text-blue-500" />
                <span className="text-sm font-semibold text-slate-700">
                  {bulkFileName
                    ? bulkFileName
                    : "Clique para selecionar um arquivo"}
                </span>
                <span className="text-xs text-slate-400">
                  Formatos aceitos: .xlsx, .xls, .csv
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleBulkFileChange}
              />
            </div>

            {bulkRows.length > 0 && (
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-slate-100">
                  <p className="text-xs font-semibold text-slate-600">
                    {bulkRows.length} registro{bulkRows.length !== 1 ? "s" : ""}{" "}
                    encontrado{bulkRows.length !== 1 ? "s" : ""}
                  </p>
                  <p className="text-xs text-emerald-600 font-semibold">
                    {bulkValidRows.length} válido
                    {bulkValidRows.length !== 1 ? "s" : ""} para importar
                  </p>
                </div>
                <div
                  className="max-h-56 overflow-y-auto divide-y divide-slate-100"
                  style={{ maxHeight: "300px" }}
                >
                  {bulkRows.map((row, idx) => (
                    <div
                      key={`${row.documento}-${idx}`}
                      className="flex items-center justify-between px-4 py-2 gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-mono text-slate-700 truncate">
                          {applyDocMask(row.documento)}
                        </p>
                        {row.nome && (
                          <p className="text-xs text-slate-400 truncate">
                            {row.nome}
                          </p>
                        )}
                      </div>
                      {row.valid && !row.duplicate ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 flex-shrink-0">
                          <MdCheckCircle size={12} />
                          Válido
                        </span>
                      ) : row.duplicate ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 flex-shrink-0">
                          <MdContentCopy size={12} />
                          Duplicado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 flex-shrink-0">
                          <MdErrorOutline size={12} />
                          Inválido
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-3">
          <div className="flex w-full justify-end gap-2">
            <button
              onClick={() => {
                setIsBulkModalOpen(false);
                setBulkText("");
                setBulkFileName("");
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleBulkSubmit}
              disabled={
                bulkCreateMutation.isLoading || bulkValidRows.length === 0
              }
              className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition"
            >
              <MdUploadFile size={16} />
              {bulkCreateMutation.isLoading
                ? "Importando..."
                : `Importar ${bulkValidRows.length || ""} cliente${bulkValidRows.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal Histórico */}
      {historyClient && (
        <HistoryModal
          client={historyClient}
          onClose={() => setHistoryClient(null)}
        />
      )}

      {/* Modal Gerenciar Carteira */}
      <Modal
        show={isCarteirModalOpen}
        onClose={() => {
          setIsCarteiraModalOpen(false);
          setEmailInput("");
          setEmailError("");
        }}
        size="lg"
      >
        <Modal.Header className="py-2">
          <div className="text-base mt-1">Gerenciar Carteira</div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-0.5">
                E-mails para notificação
              </p>
              <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                Sempre que houver uma nova alteração em qualquer cliente
                monitorado, os e-mails abaixo receberão um alerta automático.
              </p>

              {/* Input + botão adicionar */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <MdEmail
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      setEmailError("");
                    }}
                    onKeyDown={handleEmailKeyDown}
                    placeholder="exemplo@email.com"
                    className={`w-full rounded-xl border pl-8 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      emailError
                        ? "border-rose-300 bg-rose-50"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  />
                </div>
                <button
                  onClick={handleAddEmail}
                  className="inline-flex items-center gap-1 rounded-xl bg-blue-600 hover:bg-blue-700 px-3 py-2 text-sm font-semibold text-white transition flex-shrink-0"
                >
                  <IoMdAdd size={16} />
                  Adicionar
                </button>
              </div>
              {emailError && (
                <p className="mt-1.5 text-xs text-rose-500">{emailError}</p>
              )}
            </div>

            {/* Lista de e-mails */}
            {loadingEmails ? (
              <div className="flex items-center justify-center py-6 text-slate-400 text-xs gap-2">
                <svg
                  className="animate-spin h-3.5 w-3.5 text-blue-400"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Carregando e-mails...
              </div>
            ) : localEmails.length > 0 ? (
              <div className="rounded-xl border border-slate-100 bg-slate-50 divide-y divide-slate-100 overflow-hidden">
                {localEmails.map((email) => (
                  <div
                    key={email}
                    className="flex items-center justify-between px-3 py-2.5 gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <MdEmail size={12} className="text-blue-600" />
                      </div>
                      <span className="text-[13px] text-slate-700 truncate">
                        {email}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors flex-shrink-0"
                      title="Remover"
                    >
                      <IoMdClose size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 gap-1.5 text-slate-400 rounded-xl border border-dashed border-slate-200">
                <MdEmail size={22} className="opacity-30" />
                <p className="text-xs">Nenhum e-mail cadastrado ainda.</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-3">
          <div className="flex w-full justify-end gap-2">
            <button
              onClick={() => {
                setIsCarteiraModalOpen(false);
                setEmailInput("");
                setEmailError("");
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={() => saveEmailsMutation.mutate(localEmails)}
              disabled={saveEmailsMutation.isLoading}
              className="rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition"
            >
              {saveEmailsMutation.isLoading ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MonitoreClientesPage;
